import { cva } from "class-variance-authority"

export const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      // Hover styles are gated behind @media (hover:hover) and (pointer:fine)
      // (DESIGN.md §3.4 — gloves never hover): touch taps otherwise leave the
      // hover state stuck on the button until the next tap elsewhere. Call
      // sites that override a variant hover must use the same gated prefix —
      // a bare `hover:bg-*` no longer tailwind-merges against these and the
      // gated rule wins on desktop (arbitrary variants sort later in the
      // emitted CSS). `dark:hover:*` stays bare: darkMode is class-driven and
      // never enabled, so those rules are inert either way.
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        outline:
          "border-border bg-background [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "[@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive [@media(hover:hover)_and_(pointer:fine)]:hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 [@media(hover:hover)_and_(pointer:fine)]:hover:underline",
        // ── Dashboard semantic action colors (DESIGN.md §1.3/§2) ──
        // success* = the saved lane (system saved it, undoable); warning = the
        // attention lane (waiting on you); accent folds into primary (ink).
        // These encode that vocabulary so the touch-optimized pill buttons
        // route through <Button> instead of being hand-rolled. Pair with a
        // `pill*` size.
        success:
          "border-saved-line bg-white/60 text-saved-deep [@media(hover:hover)_and_(pointer:fine)]:hover:bg-white",
        successSolid:
          "bg-saved text-white [@media(hover:hover)_and_(pointer:fine)]:hover:bg-saved-press",
        warning:
          "border-attention-line bg-white text-attention-text [@media(hover:hover)_and_(pointer:fine)]:hover:border-attention-dot [@media(hover:hover)_and_(pointer:fine)]:hover:bg-attention-soft",
        accent:
          "bg-primary text-primary-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/80",
        chip: "border-border bg-card text-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:border-foreground/30 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
        // ── Dashboard pill sizes — rounded-full + 44px touch targets (the
        // phone-first/gloved-hands rule). `rounded-full` + the min-h here win
        // over the base `rounded-lg`/`h-8` via cn()'s tailwind-merge in Button. ──
        pill: "min-h-11 gap-1.5 rounded-full px-4",
        "pill-sm": "min-h-9 gap-1.5 rounded-full px-3",
        "pill-chip": "h-10 gap-1.5 rounded-full px-3.5",
        "pill-icon": "size-9 rounded-full",
        "pill-icon-sm": "size-7 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)
